import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { Dropdown, Modal } from 'antd';
import { Icon as CroplerIcon, Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';

import styles from './styles.module.scss';
import stylesDropdownMenu from '../../styles/dropdownMenu.module.scss';

import {
  ActiveBorderPolygon,
  BreakpointMobile,
  BreakpointMobileUp,
  DefaultBgPolygon,
  DefaultBorderPolygon,
  ModalWidthSmall,
} from '../../constants/ui';
import { mapBoxStyles, MapLat, MapLng } from '../../constants/keys';
import { DateFormat, DateLlFormat, LatDefault, LngDefault } from '../../constants/data';

import {
  fieldsApi,
  useDeleteSeasonMutation,
  useGetBoundariesQuery,
  useGetFieldsQuery,
} from '../../store/api/fields';
import {
  resetSelectedSeason,
  selectField,
  selectSeason,
  selectSeasonsDates,
  selectTypeBoundaries,
} from '../../store/slices/fields';
import { getCentroidCoordinates, removePolygonSources } from '../../utils/fields';
import {
  AddFieldForm,
  AddSeasonMenu,
  AddSeasonMenuModal,
  DeleteFieldForm,
  DeleteSeasonModal,
  FieldsMarker,
  FieldsSensorMarker,
  UpdateFieldForm,
} from './index.jsx';
import { useLocalStorage, useViewport, useSelectedDate } from '../../hooks';
import debounce from 'lodash.debounce';
import { Sidebar, SidebarFooter, SidebarList } from '../Sidebar/index.jsx';
import { FieldsDefaultZoom } from './constants/index.jsx';
import { SidebarHeader } from '../Sidebar/index.jsx';
import { useGetSensorsDatesQuery, useGetSensorsSummaryQuery } from '../../store/api/sensors';
import { FilterDate } from '../Filters/index.jsx';
import { selectDate } from '../../store/slices/sensors';
import { changeZoom } from '../../store/slices/ui';
import useEditOrDuplicateSeason from './hooks/useOnClickEditSeason.jsx';
import { GetSeasonDisabledDates } from './utils/index.jsx';
import { getSidebarLinksMap } from '../../utils/ui';
import { getCropName } from '../../utils/crops';

const Fields = ({ t }) => {
  const SidebarLinksMap = getSidebarLinksMap(t);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { widthViewport } = useViewport();
  const [mapLng, setMapLng] = useLocalStorage(MapLng, '');
  const [mapLat, setMapLat] = useLocalStorage(MapLat, '');
  const todayDate = dayjs().set('hour', 12);
  const [searchParams, setSearchParams] = useSearchParams();
  const { zoom } = useSelector((state) => state.ui);

  const mapContainer = useRef(null);
  const map = useRef(null);

  const { crops } = useSelector((state) => state.catalog);
  const { currentUserRole } = useSelector((state) => state.auth);
  const { selectedSeason, selectedDate } = useSelector((state) => state.sensors);

  const [currentZoom, setCurrentZoom] = useState(zoom);
  const [modalAddFieldOpen, setModalAddFieldOpen] = useState(false);
  const [modalUpdateFieldOpen, setModalUpdateFieldOpen] = useState(false);
  const [modalDeleteFieldOpen, setModalDeleteFieldOpen] = useState(false);
  const [modalDeleteSeasonOpen, setModalDeleteSeasonOpen] = useState(false);
  const [modalAddSeasonMenuOpen, setModalAddSeasonMenuOpen] = useState(false);
  const [editedField, setEditedField] = useState({ id: '', name: '' });

  const [activeSeasons, setActiveSeasons] = useState([]);
  const [editedSeason, setEditedSeason] = useState({ id: '', name: '', sowingDate: '', harvestDate: '' });
  const [zoomSeason, setZoomSeason] = useState();
  const [sourceSeasons, setSourceSeasons] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [markersSensors, setMarkersSensors] = useState([]);
  const [changeCenter, setChangeCenter] = useState(false);

  const [styleMapLoaded, setStyleMapLoaded] = useState(false);

  useSelectedDate([todayDate.format(DateFormat)]);

  const [deleteSeason, { isSuccess: isDeleteSeasonSuccess }] = useDeleteSeasonMutation();
  const { data: fields, isSuccess: isFieldsSuccess } = useGetFieldsQuery();
  const { data: boundaries, isSuccess: isBoundariesSuccess } = useGetBoundariesQuery();
  const { data: sensors, isSuccess: isSensorsSuccess } = useGetSensorsSummaryQuery(
    { date: selectedDate },
    { skip: !selectedDate, refetchOnMountOrArgChange: true },
  );
  const { data: sensorsDates = [] } = useGetSensorsDatesQuery();

  // Create a new field
  const openAddFieldModal = () => {
    setModalAddFieldOpen(true);
  };
  const closeAddFieldModal = () => {
    setModalAddFieldOpen(false);
  };

  // Update field
  const openUpdateFieldModal = ({ id, name }) => {
    setEditedField({ id, name });
    setModalUpdateFieldOpen(true);
  };
  const closeUpdateFieldModal = () => {
    setModalUpdateFieldOpen(false);
  };

  // Delete field
  const closeDeleteFieldModal = () => {
    setModalDeleteFieldOpen(false);
  };
  const openDeleteFieldModal = ({ id, name }) => {
    setEditedField({ id, name });
    setModalDeleteFieldOpen(true);
  };

  // Open season detail page (add/edit)
  const goToAddSeason = ({ isVardaView, id }) => {
    dispatch(selectField({ id }));
    dispatch(selectTypeBoundaries(isVardaView));
    dispatch(resetSelectedSeason());
    dispatch(selectSeasonsDates({ dates: GetSeasonDisabledDates({ fields, id, seasonId: '', isEdit: false }) }));
    dispatch(changeZoom(currentZoom));
    navigate('/season');
  };

  // Get data for edit/duplicate season
  const editDuplicateSeason = useEditOrDuplicateSeason();

  // Click duplicate season
  const onDuplicateSeason = ({ season, fieldId }) => {
    editDuplicateSeason({
      season,
      fieldId,
      isEdit: false,
      currentZoom,
      disabledDates: GetSeasonDisabledDates({ fields, id: fieldId, seasonId: season.id, isEdit: false }),
    });
  };

  // Click edit season
  const onEditSeason = ({ season, fieldId }) => {
    editDuplicateSeason({
      season,
      fieldId,
      isEdit: true,
      currentZoom,
      disabledDates: GetSeasonDisabledDates({ fields, id: fieldId, seasonId: season.id, isEdit: true }),
    });
  };

  // Delete season
  const onDeleteSeason = () => {
    deleteSeason({ seasonId: editedSeason.id });
    closeDeleteSeasonModal();
  };
  const closeDeleteSeasonModal = () => {
    setModalDeleteSeasonOpen(false);
  };
  const openDeleteSeasonModal = ({ id, name, sowingDate, harvestDate }) => {
    setEditedSeason({ id, name, sowingDate, harvestDate });
    setModalDeleteSeasonOpen(true);
  };

  // Click Add season (mobile)
  const onClickAddSeasonMobile = ({ id, name }) => {
    setModalAddSeasonMenuOpen(true);
    setEditedField({ id, name });
  };

  // Click season
  const onClickSeason = ({ id, boundaryId, crop, date, fieldName, fieldId }) => {
    let updatedActiveSeasons = [...activeSeasons];

    if (!activeSeasons.find((item) => item.boundaryId === boundaryId)) {
      // Show only one season for field
      updatedActiveSeasons = updatedActiveSeasons.filter((obj) => obj.fieldId !== fieldId);
      // Add season on map
      const seasonBoundary = boundaries.filter((obj) => obj.id === boundaryId)[0];
      if (seasonBoundary) {
        updatedActiveSeasons.push({
          id,
          boundaryId,
          crop,
          coordinates: seasonBoundary.geom.coordinates,
          date,
          fieldName,
          fieldId,
        });
        setZoomSeason(id);
        const coordinates = seasonBoundary.geom.coordinates[0][0][0];
        setMapLng(coordinates[0]);
        setMapLat(coordinates[1]);
      }
    } else {
      setZoomSeason(id);
      const seasonBoundary = boundaries.filter((obj) => obj.id === boundaryId)[0];
      const coordinates = seasonBoundary.geom.coordinates[0][0][0];
      setMapLng(coordinates[0]);
      setMapLat(coordinates[1]);
    }

    setActiveSeasons(updatedActiveSeasons);
    setChangeCenter(true);
    dispatch(selectSeason(id));
  };

  const onChangeDate = (date) => {
    dispatch(selectDate(date));
    searchParams.set('date', date);
    setSearchParams(searchParams);
  };

  // Init mapbox (only once)
  useEffect(() => {
    if (map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapBoxStyles,
      center: mapLng && mapLat ? [mapLng, mapLat] : [LngDefault, LatDefault],
      zoom: zoom || FieldsDefaultZoom,
      hash: true,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const handleZoom = debounce(() => {
      const currentZoom = map.current.getZoom();
      setCurrentZoom(currentZoom);
    }, 100);

    map.current.on('zoom', handleZoom);

    map.current.on('style.load', () => {
      setStyleMapLoaded(true);
    });

    map.current.on('moveend', () => {
      const center = map.current.getCenter();
      setMapLat(center.lat);
      setMapLng(center.lng);
    });
  }, []);

  // Seasons on map
  useEffect(() => {
    if (!isFieldsSuccess || !isBoundariesSuccess) {
      return;
    }
    const seasonsForMap = [];
    fields.map((item) => {
      if (item.seasons.length) {
        const listActiveSeasons = [];
        item.seasons.map((obj) => {
          const sowingDate = dayjs(obj.sowing_date);
          const harvestDate = dayjs(obj.harvest_date || todayDate);
          const today = dayjs(dayjs().format(DateFormat));

          if (sowingDate.isBefore(today) || sowingDate.isSame(today) || selectedSeason === obj.id) {
            if (!harvestDate || harvestDate.isAfter(today) || harvestDate.isSame(today) || selectedSeason === obj.id) {
              const seasonBoundary = boundaries.filter((boundary) => boundary.id === obj.boundary_id)[0];
              if (seasonBoundary) {
                listActiveSeasons.push({
                  id: obj.id,
                  boundaryId: obj.boundary_id,
                  crop: obj.crop_id,
                  coordinates: seasonBoundary.geom.coordinates, //seasonBoundary.geom.coordinates[0][0]
                  date: obj.sowing_date,
                  fieldName: item.name,
                  fieldId: item.id,
                });
              }
            }
          }
        });

        // if there are no current seasons
        if (!listActiveSeasons.length) {
          const seasonBoundary = boundaries.filter((boundary) => boundary.id === item.seasons[0].boundary_id)[0];
          if (seasonBoundary) {
            listActiveSeasons.push({
              id: item.seasons[0].id,
              boundaryId: item.seasons[0].boundary_id,
              crop: item.seasons[0].crop_id,
              coordinates: seasonBoundary.geom.coordinates, //seasonBoundary.geom.coordinates[0][0]
              date: item.seasons[0].sowing_date,
              fieldName: item.name,
              fieldId: item.id,
            });
          }
        }
        seasonsForMap.push(...listActiveSeasons);
      }
    });
    setActiveSeasons(seasonsForMap);
    setZoomSeason(selectedSeason ? selectedSeason : seasonsForMap.length ? seasonsForMap[0].id : '');
  }, [isFieldsSuccess, isBoundariesSuccess, boundaries, fields]);

  // Add markers, polygons
  useEffect(() => {
    if (activeSeasons.length && sensors && styleMapLoaded) {
      if (markers.length) {
        markers.forEach((marker) => marker.remove());
        setMarkers([]);
      }

      if (sourceSeasons.length) {
        sourceSeasons.forEach((source) => {
          removePolygonSources(source.id, source.layerFillId, source.layerOutlineId, map.current);
        });
        setSourceSeasons([]);
      }

      const mapMarkers = [];
      const listSource = [];

      activeSeasons.map((item, index) => {
        const colorCrop = crops.filter((crop) => crop.id === item.crop)[0].color;

        // Polygon
        map.current.addSource(`polygon-${index}`, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: item.coordinates[0],
            },
          },
        });

        map.current.addLayer({
          id: `polygon-fill-${index}`,
          type: 'fill',
          source: `polygon-${index}`,
          layout: {},
          paint: {
            'fill-color': item.id === zoomSeason ? colorCrop || DefaultBgPolygon : DefaultBgPolygon,
            'fill-opacity': 0.4,
          },
        });

        map.current.addLayer({
          id: `polygon-outline-${index}`,
          type: 'line',
          source: `polygon-${index}`,
          layout: {},
          paint: {
            'line-color': item.id === zoomSeason ? ActiveBorderPolygon : DefaultBorderPolygon,
            'line-width': 2,
            'line-dasharray': item.id === zoomSeason ? [1, 1] : [],
          },
        });

        map.current.on('click', `polygon-fill-${index}`, () => {
          setZoomSeason(item.id);
          onClickSeason({
            id: item.id,
            boundaryId: item.boundaryId,
            crop: item.crop,
            date: item.date,
            fieldName: item.name,
            fieldId: item.id,
          });
        });

        // Marker
        const centroid = getCentroidCoordinates(item.coordinates[0][0]);
        const ref = React.createRef();
        ref.current = document.createElement('div');
        createRoot(ref.current).render(
          <FieldsMarker text={getCropName({ t, id: item.crop })} date={item.date} fieldName={item.fieldName} t={t} />,
        );
        const marker = new mapboxgl.Marker(ref.current).setLngLat(centroid).addTo(map.current);

        mapMarkers.push(marker);
        listSource.push({
          id: `polygon-${index}`,
          layerFillId: `polygon-fill-${index}`,
          layerOutlineId: `polygon-outline-${index}`,
        });
      });

      if (changeCenter || !mapLng || !mapLat) {
        const bounds = new mapboxgl.LngLatBounds();
        const zoomPolygon = activeSeasons.filter((obj) => obj.id === zoomSeason);
        if (zoomPolygon.length) {
          zoomPolygon[0].coordinates[0][0].forEach((coord) => {
            bounds.extend(coord);
          });
        }

        if (bounds && bounds._sw) {
          setMapLng(bounds._sw.lng);
          setMapLat(bounds._sw.lat);

          map.current.fitBounds(bounds, {
            zoom: currentZoom,
          });
        }

        setChangeCenter(false);
      }

      setMarkers(mapMarkers);
      setSourceSeasons(listSource);
    }
  }, [activeSeasons, styleMapLoaded, sensors]);

  // Get sensors markers
  useEffect(() => {
    if (!isSensorsSuccess || !sensors || !styleMapLoaded) return;
    if (markersSensors.length) {
      markersSensors.forEach((marker) => marker.remove());
      setMarkersSensors([]);
    }

    const mapMarkers = [];
    sensors.map((item) => {
      const sensor = item.location.sensor;
      const gps = sensor && sensor.gps_point ? sensor.gps_point : item.location.install_point;
      if (gps && gps.coordinates) {
        const ref = React.createRef();
        ref.current = document.createElement('div');
        createRoot(ref.current).render(
          <FieldsSensorMarker name={item.location && item.location.name ? item.location.name : t('empty.noName')} />,
        );
        const marker = new mapboxgl.Marker(ref.current).setLngLat(gps.coordinates).addTo(map.current);
        mapMarkers.push(marker);
      }
    });
    setMarkersSensors(mapMarkers);
  }, [isSensorsSuccess, sensors, styleMapLoaded]);

  useEffect(() => {
    if (isDeleteSeasonSuccess) {
      setTimeout(() => {
        dispatch(fieldsApi.util.resetApiState());
      });
    }
  }, [isDeleteSeasonSuccess]);

  return (
    <>
      <div className={`workspace ${currentZoom > 15.2 ? styles.mapZoom : ''}`}>
        <Sidebar>
          {/*Mobile View*/}
          {widthViewport < BreakpointMobileUp && (
            <SidebarHeader
              title={SidebarLinksMap.fields.title}
              text={SidebarLinksMap.fields.description}
              count={fields ? fields.length : 0}
            >
              <Button type="primary" icon={<CroplerIcon name="add" />} onClick={() => openAddFieldModal()} />
            </SidebarHeader>
          )}

          <SidebarList
            countText={widthViewport > BreakpointMobile ? `${fields ? fields.length : 0} ${t('fields.title')}` : null}
          >
            {!!fields && (
              <ul className={`${styles.fieldList} custom-scroll`}>
                {fields.map((item) => {
                  return (
                    <li
                      className={`${styles.fieldItem} ${item.seasons.find((s) => s.id === zoomSeason) ? styles.fieldItemSelected : ''}`}
                      key={item.id}
                    >
                      {/*Field*/}
                      <div className={styles.fieldItemTitle}>
                        <span>{item.name}</span>
                        {/*Edit*/}
                        {(currentUserRole === 'owner' || currentUserRole === 'editor') && (
                          <Button
                            size="small"
                            type="default"
                            icon={<CroplerIcon name="edit" />}
                            onClick={() => openUpdateFieldModal({ id: item.id, name: item.name })}
                          />
                        )}
                        {/*Remove Field*/}
                        {(currentUserRole === 'owner' || currentUserRole === 'editor') &&
                          (!item.seasons || item.seasons.length === 0) && (
                            <Button
                              size="small"
                              type="default"
                              icon={<CroplerIcon name="remove" />}
                              onClick={() => openDeleteFieldModal({ id: item.id, name: item.name })}
                            />
                          )}
                      </div>

                      {/*Seasons*/}
                      {item.seasons && item.seasons.length > 0 && (
                        <div className={styles.optionsList}>
                          {item.seasons.map((season) => (
                            <div
                              className={`
                                ${styles.options} 
                                ${activeSeasons.find((obj) => obj.id === season.id) ? styles.optionsActive : ''}
                            
                              `}
                              key={season.id}
                            >
                              {(currentUserRole === 'owner' || currentUserRole === 'editor') && (
                                <Dropdown
                                  menu={{
                                    items: [
                                      {
                                        key: '1', // Duplicate season
                                        label: (
                                          <Button
                                            type="default"
                                            className={stylesDropdownMenu.dropdownMenuLink}
                                            onClick={() => onDuplicateSeason({ season, fieldId: item.id })}
                                          >
                                            <CroplerIcon name="copy" />
                                            <span>{t('fields.menu.duplicate')}</span>
                                          </Button>
                                        ),
                                      },
                                      {
                                        key: '2', // Edit season
                                        label: (
                                          <Button
                                            type="default"
                                            className={stylesDropdownMenu.dropdownMenuLink}
                                            onClick={() => onEditSeason({ season, fieldId: item.id })}
                                          >
                                            <CroplerIcon name="editLine" />
                                            <span>{t('actions.edit')}</span>
                                          </Button>
                                        ),
                                      },
                                      {
                                        key: '3', // Delete season
                                        label: (
                                          <Button
                                            type="default-danger"
                                            className={stylesDropdownMenu.dropdownMenuLink}
                                            onClick={() =>
                                              openDeleteSeasonModal({
                                                id: season.id,
                                                name: season.crop_id,
                                                sowingDate: season.sowing_date,
                                                harvestDate: season.harvest_date,
                                              })
                                            }
                                          >
                                            <CroplerIcon name="remove" />
                                            <span>{t('actions.delete')}</span>
                                          </Button>
                                        ),
                                      },
                                    ],
                                  }}
                                >
                                  <span className={styles.fieldItemMore}>
                                    <Button
                                      icon={<CroplerIcon name="moreHorizontal" />}
                                    />
                                  </span>
                                </Dropdown>
                              )}
                              <button
                                className={styles.optionsItems}
                                onClick={() =>
                                  onClickSeason({
                                    id: season.id,
                                    boundaryId: season.boundary_id,
                                    crop: season.crop_id,
                                    date: season.sowing_date,
                                    fieldName: item.name,
                                    fieldId: item.id,
                                  })
                                }
                              >
                                <div className={styles.optionsItem}>
                                  <div
                                    className={styles.optionsItemIndicator}
                                    style={{
                                      backgroundColor: crops
                                        ? crops.filter((c) => c.id === season.crop_id)[0].color
                                        : 'black',
                                    }}
                                  ></div>
                                  {getCropName({ t, id: season.crop_id })}
                                </div>
                                <div className={styles.optionsItem}>
                                  {dayjs(season.sowing_date).format(DateLlFormat)} -{' '}
                                  {dayjs(season.harvest_date).format(DateLlFormat)}
                                </div>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/*Add Season Menu*/}
                      {(currentUserRole === 'owner' || currentUserRole === 'editor') &&
                      widthViewport > BreakpointMobile ? (
                        <AddSeasonMenu
                          onPickMap={() => goToAddSeason({ isVardaView: true, id: item.id })}
                          onCreateManually={() => goToAddSeason({ isVardaView: false, id: item.id })}
                          t={t}
                        />
                      ) : (
                        <Button
                          className={styles.fieldItemAdd}
                          type="text"
                          size="small"
                          icon={<CroplerIcon name="add" />}
                          onClick={() => onClickAddSeasonMobile({ id: item.id, name: item.name })}
                        >
                          {t('fields.buttonNewSeason')}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </SidebarList>

          {widthViewport > BreakpointMobile && (currentUserRole === 'owner' || currentUserRole === 'editor') && (
            <SidebarFooter>
              <Button type="primary" icon={<CroplerIcon name="add" />} onClick={() => openAddFieldModal()}>
                {t('fields.addField')}
              </Button>
            </SidebarFooter>
          )}
        </Sidebar>
        {sensorsDates && sensorsDates.length > 0 && (
          <div className={styles.fieldsDate}>
            <FilterDate
              allDates={sensorsDates}
              selectedDate={selectedDate}
              selectDate={onChangeDate}
              isDarkTheme={true}
            />
            <span dangerouslySetInnerHTML={{__html: t('fields.dateText')}}></span>
          </div>
        )}
        <div ref={mapContainer} className={`workspace-body map-fields`}></div>
      </div>

      {/*Add Field*/}
      <Modal
        centered
        title={t('fields.addField')}
        footer={false}
        onCancel={() => closeAddFieldModal()}
        open={modalAddFieldOpen}
        width={ModalWidthSmall}
      >
        <AddFieldForm fields={fields} closeModal={() => closeAddFieldModal()} t={t} />
      </Modal>

      {/*Rename Field*/}
      <Modal
        centered
        title={`${t('fields.rename.title')} ${editedField.name}`}
        footer={false}
        onCancel={() => closeUpdateFieldModal()}
        open={modalUpdateFieldOpen}
        width={ModalWidthSmall}
      >
        <UpdateFieldForm fields={fields} editedField={editedField} closeModal={() => closeUpdateFieldModal()} t={t} />
      </Modal>

      {/*Delete Field*/}
      <Modal
        title={t('common.areYouSure')}
        open={modalDeleteFieldOpen}
        onCancel={() => closeDeleteFieldModal()}
        footer={false}
        centered
        width={ModalWidthSmall}
      >
        <DeleteFieldForm editedField={editedField} closeModal={() => closeDeleteFieldModal()} t={t} />
      </Modal>

      {/*Delete Season*/}
      <DeleteSeasonModal
        modalOpen={modalDeleteSeasonOpen}
        closeModal={closeDeleteSeasonModal}
        editedSeason={editedSeason}
        onDeleteSeason={onDeleteSeason}
        t={t}
      />

      {/*Add Season Menu (Mobile)*/}
      <AddSeasonMenuModal
        fieldName={editedField.name}
        modalOpen={modalAddSeasonMenuOpen}
        closeModal={() => setModalAddSeasonMenuOpen(false)}
        onPickMap={() => goToAddSeason({ isVardaView: true, id: editedField.id })}
        onCreateManually={() => goToAddSeason({ isVardaView: false, id: editedField.id })}
        t={t}
      />
    </>
  );
};

export default Fields;
